import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  editPodcast,
  fetchDraftPodcast,
  fetchPodcastTranscription,
  invalidateGenerateTranscriptionPodcastState,
  invalidateQuickGeneratePodcastState,
  invalidatePublishPodcast,
  publishPodcast,
  quickGeneratePodcast,
  syncPodcastProcessingState,
  updatePodcast,
  updatePodcastProperty,
  updateValidationErrorsForPodcast,
  validatePodcast,
} from "src/redux/actions/coaching/podcast";

import { Podcast } from "src/types/coaching/podcast";
import { PodcastMenuBar } from "../components/menu";

import { useTransition, animated, useSpring } from "@react-spring/web";
import TextareaAutosize from "react-autosize-textarea";
import Reveal from "src/components/animation";
import { CourseLearners } from "src/pages/coaching/manage/courses/edit/components/learners";
import { AddLearnersDialog } from "src/pages/coaching/manage/courses/edit/components/learners/add";
import { SelectSkillsDialog } from "src/pages/coaching/manage/courses/edit/components/selectSkills";
import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";
import { formatTime, preparePodcastRequest, validatePodcastRequest } from "./helper";
import { AddAssetsDialog } from "src/components/asset/add";

import userRoleService from "src/services/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { PodcastNotFound } from "src/pages/coaching/components/notFound";
import { EditInStudio } from "../components/studio";
import { BannerView } from "src/components/banner";
import { Banner } from "src/types/coaching/courses";
import { EditCourseThumbnail } from "src/pages/coaching/manage/courses/edit/thumbnail";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import EmptyMessage from "src/components/emptyMessage";

import _ from "lodash";
import cx from "classnames";

import styles from "./edit.module.scss";
import { LoadingPodcastPage } from "src/pages/coaching/components/podcast";
import { FileService } from "src/services/file.service";
import { PodcastPlayer } from "src/pages/coaching/components/podcast/components/player";
import useMeasure from "react-use-measure";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";


export function EditPodcastPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id as string;

  const persistedPodcast: Podcast = useSelector(
    (state: any) =>
      _.get(state.coaching.podcasts, `podcast[${id}].item`) as Podcast,
  );

  const loading = useSelector((state: any) => {
    return _.get(state, `coaching.podcasts.podcast[${id}].loading`);
  });

  const loaded = useSelector((state: any) => {
    return _.get(state, `coaching.podcasts.podcast[${id}].loaded`, false);
  });

  const podcast: Podcast = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.item`, null) as Podcast;
  });

  const podcastErrorCode = useSelector((state: any) => {
    return _.get(
      state,
      `coaching.podcasts.podcast[${id}].error.errors[0].code`,
    );
  });

  const emptyPodcast = useMemo(() => {
    const topics = _.get(podcast, "topics", []);
    return _.size(topics) === 0;
  }, [podcast]);

  const { publishing, published } = useSelector((state: any) => {
    return {
      publishing: _.get(state.coaching.podcasts, 'publish.publishing'),
      published: _.get(state.coaching.podcasts, 'publish.published')
    }
  });

  const transcripts = useSelector((state: any) => {
    const transcription = _.get(state.coaching.podcasts, `edit.item.transcription`, null);
    if (transcription) {
      return transcription.data;
    }
    return [];
  });

  const [showSkillSlider, setShowSkillSlider] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null,
  });

  const quickGeneratingPodcast = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const transcriptGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.generateTranscript.generated`, null);
  });

  const audioGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.generateAudio.generated`, null);
  });

  const editInStudio = useMemo(() => {
    return menuState.secondaryAction === "edit-studio";
  }, [menuState]);


  const editTransitions = useTransition(!isPublished, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const editStudioTransition = useTransition(editInStudio, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const emptyTransitions = useTransition((emptyPodcast || quickGeneratingPodcast), {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const detailsTransitions = useTransition(!(emptyPodcast || quickGeneratingPodcast), {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });


  const handleMenuStateChange = (state) => {
    setMenuState(state);
  };

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updatePodcastProperty({
        podcast: podcast.id!,
        property,
        value,
      }),
    );
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  };

  const handleSkillRemove = (skill) => {
    const items = podcast?.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };

  const handleSave = () => {
    const validationErrors = validatePodcastRequest(podcast);
    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForPodcast({
          podcast: podcast.id!,
          errors: validationErrors,
        }),
      );
    }

    const request = preparePodcastRequest(persistedPodcast, podcast);

    dispatch(
      updatePodcast({
        id: podcast.id!,
        request: request,
      }),
    );
  };

  const handlePublish = () => {
    const validationErrors = validatePodcastRequest(podcast);

    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForPodcast({
          podcast: podcast.id!,
          errors: validationErrors,
        }),
      );

      return;
    }

    dispatch(publishPodcast(podcast.id!));
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchDraftPodcast(id));

      const podcastLearnerRequest = {
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      };
      dispatch(fetchCourseLearners(podcastLearnerRequest));
    }
  }, [id]);

  useEffect(() => {
    if (persistedPodcast && persistedPodcast.id !== podcast?.id) {
      dispatch(editPodcast(persistedPodcast));
      dispatch(validatePodcast(persistedPodcast.id!));
    }
  }, [persistedPodcast, podcast]);

  useEffect(() => {
    if (menuState.primaryAction === "save") {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    } else if (menuState.primaryAction === "publish") {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    }
  }, [menuState]);

  useEffect(() => {
    if ((transcriptGenerated || audioGenerated) && podcast.id) {
      dispatch(syncPodcastProcessingState(podcast.id!));
    }

    if (transcriptGenerated && podcast.id) {
      dispatch(invalidateGenerateTranscriptionPodcastState(podcast.id!));
    }

    if (quickGeneratingPodcast && audioGenerated && podcast.id) {
      dispatch(invalidateQuickGeneratePodcastState(podcast.id!));
    }

  }, [transcriptGenerated, audioGenerated, quickGeneratingPodcast]);

  useEffect(() => {
    if (published) {
      setIsPublished(true);
    }
  }, [published]);

  useEffect(() => {
    if (!emptyPodcast) {
      dispatch(fetchPodcastTranscription(podcast.id!));
    }
  }, [emptyPodcast]);

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (podcastErrorCode === 10007) {
    return (
      <PodcastNotFound
        onBack={() => {
          navigate(`/coaching/manage`);
        }}
      />
    );
  }

  if ((loading && podcast?.id != id) || !podcast) {
    return (
      <LoadingPodcastPage onBack={() => {
        navigate(`/coaching/manage`);
      }} />
    );
  }

  return (
    <>
      <div key={id} className={styles.podcastContainer}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.back}
              onClick={() => {
                navigate(`/coaching/studio`);
              }}
            >
              <i className="paperflite-left-arrow"></i>
              <span>Studio</span>
            </div>
          </div>
          <div className={styles.menubar}>
            <PodcastMenuBar
              podcast={podcast}
              menuState={menuState}
              onMenuStateChange={handleMenuStateChange}
            />
          </div>
        </div>

        {editTransitions(
          (transitionStyles, item) =>
            item && (
              <animated.div className={styles.editContainer} style={transitionStyles}>
                <div className={styles.top}>
                  <div className={styles.left}>
                    <PodcastHeader podcast={podcast} />

                    <div className={styles.detailsContainer}>
                      {emptyTransitions((style, item) =>
                        item && (
                          <animated.div className={styles.stack} style={style}>
                            <UploadAssetContainer podcast={podcast} />
                          </animated.div>
                        )
                      )}

                      {detailsTransitions((style, item) =>
                        item && (
                          <animated.div className={styles.stack} style={style}>
                            <PodcastDetailContainer podcast={podcast} onChange={handlePropertyChange} />
                          </animated.div>
                        )
                      )}
                    </div>
                  </div>

                  <div className={styles.right}>
                    <div className={styles.skillsContainer}>
                      <p className={styles.heading}>
                        Skills, learner will get ({podcast?.skills?.length || 0})
                      </p>
                      <div className={styles.skills}>
                        <div
                          className={styles.addSkillButton}
                          onClick={() => onSkillClicked()}
                        >
                          <i className={cx(styles.icon, "paperflite-add")}></i>
                        </div>

                        {(podcast?.skills || []).map((skill: any) => {
                          return (
                            <div key={skill.id} className={styles.skill}>
                              <div className={styles.name}>{skill.name}</div>
                              <i
                                className={cx(
                                  "paperflite-close",
                                  styles.closeIcon,
                                )}
                                onClick={() => handleSkillRemove(skill)}
                              ></i>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={styles.courseLearnersContainer}>
                      <CourseLearners id={podcast.id!} />
                    </div>
                    {transcripts && _.size(transcripts) > 0 && (
                      <PodcastTranscripts transcripts={transcripts} />
                    )}
                  </div>

                </div>
                {podcast?.audio?.url && (
                  <div className={styles.bottom}>
                    <PodcastPlayer
                      podcast={podcast}
                      initialTimeSpent={0}
                      autoPlay={false}
                      onPlay={() => { }}
                      onAnalyticsChange={() => { }}
                    />
                  </div>
                )}
              </animated.div>
            ),
        )}
      </div>

      {editStudioTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.editInStudioContainer)}>
            <EditInStudio
              podcast={podcast}
              menuState={menuState}
              onMenuStateChange={handleMenuStateChange}
            />
          </animated.div>
        ) : false;
      })}

      {showSkillSlider && (
        <SelectSkillsDialog
          show={showSkillSlider}
          selected={podcast?.skills || []}
          onFinish={handleAddSkillsFinish}
          onClose={() => setShowSkillSlider(false)}
        />
      )}

      <AddLearnersDialog
        show={showAddLearner}
        course={podcast}
        onFinish={() => setShowAddLearner(false)}
        onClose={() => setShowAddLearner(false)}
      />

      <PublishView isOpen={isPublished} onClose={() => setIsPublished(false)} />
    </>
  );
}

interface PublishViewProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PublishView: React.FC<PublishViewProps> = ({
  isOpen,
  onClose
}) => {
  const PublishCourseIcon = "/images/coaching/publish.svg";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [closePublish, setClosePublish] = useState(false);

  const publishTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 100,
    onRest: () => {
      if (closePublish) {
        setClosePublish(false);
        dispatch(invalidatePublishPodcast());
        onClose();
      }
    },
  });

  useEffect(() => {
    return () => {
      dispatch(invalidatePublishPodcast());
    };
  }, []);


  return publishTransitions(
    (transitionStyles, item) =>
      item && (
        <animated.div
          className={styles.publishContainer}
          style={transitionStyles}
        >
          <EmptyMessage
            width={500}
            image={PublishCourseIcon}
            heading="Congratulations!"
            message="Your podcast has been published."
          />
          <div className={styles.actions}>
            <Button
              variant="gradient"
              icon="paperflite-right-arrow"
              text="View Podcast Details"
              onClick={() => setClosePublish(true)}
            />
            <Button
              variant="secondary"
              icon="paperflite-close"
              text="Exit"
              onClick={() => navigate(`/coaching/manage`)}
            />
          </div>
        </animated.div>
      ),
  );
};

interface UploadAssetContainerProps {
  podcast: Podcast;
}

const UploadAssetContainer: React.FC<UploadAssetContainerProps> = ({ podcast }) => {
  const dispatch = useDispatch();
  const [showAddAssets, setShowAddAssets] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);

  const icons = useMemo(() => {
    return _.map(assets, (asset) => {
      return asset.icon
    });
  }, [assets]);

  const quickGenerating = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const processingTranscript = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateTranscript.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.transcriptProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const processingAudio = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateAudio.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.audioProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const currentStep = useMemo(() => {
    if (processingAudio) {
      return "Generating audio...";
    } else if (processingTranscript) {
      return "Generating transcripts...";
    }

    return "Processing your assets...";
  }, [quickGenerating, processingTranscript, processingAudio]);

  const generating = quickGenerating || processingTranscript || processingAudio;

  const emptyTransition = useTransition(!generating, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const progressTransition = useTransition(generating, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const handleAddAsset = () => {
    setShowAddAssets(true);
  }

  const handleFinishAddAssets = (assets: any[]) => {
    const invalidAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES"];
      return !_.includes(allowedTypes, fileType);
    });

    if (_.size(invalidAssets) > 0) {
      toast.error("Only pptx, pdf assets can be added to podcasts");
      return false;
    }

    const filteredAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES"];
      return _.includes(allowedTypes, fileType);
    });

    if (_.size(filteredAssets) === 0) {
      toast.error("No valid assets selected. Please select pptx, pdf assets.");
      return false;
    }

    if (_.size(filteredAssets) > 5) {
      toast.error("You can only add a maximum of 5 assets to a podcast.");
      return false;
    }

    if (filteredAssets.length > 0) {
      setAssets(filteredAssets);
      handleCloseAddAssets();

      dispatch(
        quickGeneratePodcast({
          id: podcast!.id!,
          request: {
            assets: filteredAssets.map((asset) => asset.id),
          },
          generateAudio: true,
        }),
      );
    }

    return true;
  }

  const handleCloseAddAssets = () => {
    setShowAddAssets(false);
  }

  const entityAction = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES"];
      const disabled =
        fileType && !_.includes(allowedTypes, fileType);
      return (
        <Tooltip
          content="Only pptx, pdf assets can be added to podcasts"
          placement="auto"
          disabled={!disabled}
        >
          <button className={styles.action}>
            <i
              className={cx(
                styles.icon,
                disabled ? "paperflite-lock" : "paperflite-add",
              )}
            ></i>
          </button>
        </Tooltip>
      );
    }
    return false;
  };

  const assetSelectionFilter = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES"];
      return _.includes(allowedTypes, fileType);
    }
    return false;
  }

  return (
    <div className={styles.quickGenerateContainer}>
      {emptyTransition(
        (style, item) =>
          item && (
            <animated.div className={cx(styles.addAssetsContainer, styles.stack)} style={style}>
              <div className={styles.inner}>
                <img
                  className={styles.icon}
                  src="/images/coaching/podcast/upload_assets.svg"
                  alt="podcast"
                />
                <div className={styles.message}>
                  <span className={styles.title}>
                    Upload your assets here to kick off your podcast journey!
                  </span>
                  <p className={styles.description}>
                    After uploading the source, you&apos;ll have the option to select the
                    time, mood, and tone.
                  </p>
                </div>

                <Button
                  variant="gradient"
                  className={styles.addAssetsButton}
                  text="Add assets"
                  icon="paperflite-right-arrow"
                  onClick={handleAddAsset}
                />
              </div>
              <AddAssetsDialog
                show={showAddAssets}
                selectableEntityTypes={["asset"]}
                source={{
                  type: "course",
                  entity: podcast as any,
                }}
                allowBackgroundUpload={false}
                entityAction={entityAction}
                selectionFilter={assetSelectionFilter}
                onFinish={handleFinishAddAssets}
                onClose={handleCloseAddAssets}
              />
            </animated.div>
          )
      )}

      {progressTransition(
        (style, item) =>
          item && (
            <animated.div className={cx(styles.processingContainer, styles.stack)} style={style}>
              <div className={styles.inner}>
                <div className={styles.assets}>
                  {icons.map((icon, index) => (
                    <div key={index} className={styles.asset}>
                      <Thumbnail
                        className={styles.thumbnail}
                        media={icon}
                        defaultImage="/images/assets/default_asset.jpg"
                      />
                    </div>
                  ))}
                </div>

                <div className={styles.message}>
                  <span className={styles.title}>
                    We’re checking your assets—just a moment!
                  </span>
                  <p className={styles.description}>
                    This may take a few minutes. Feel free to go
                    back—we’ll notify <br />
                    you once it’s ready.
                  </p>
                </div>
                <div className={styles.progressWrapper}>
                  <div className={styles.progress}>
                    <div className={styles.bar} />
                  </div>
                  <div className={styles.step}>
                    {currentStep}
                  </div>
                </div>
              </div>
            </animated.div>
          )
      )}
    </div>
  );
}

interface PodcastHeaderProps {
  podcast: Podcast;
}

const PodcastHeader: React.FC<PodcastHeaderProps> = ({ podcast }) => {
  const nameInputRef = React.useRef<any>(null);

  const dispatch = useDispatch();

  const name = useMemo(() => {
    return _.get(podcast, "name") || "";
  }, [podcast]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "name" });
  });

  const thumbnailError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const banner = useMemo(() => {
    return _.get(podcast, "banner");
  }, [podcast]);

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const [editThumbnail, setEditThumbnail] = useState(false);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updatePodcastProperty({
        podcast: podcast.id!,
        property,
        value,
      }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const podcastDuration = useMemo(() => {
    const duration = _.get(podcast, "audio.duration") || 0.0;
    if (duration > 0) {
      return FileService.formatDuration(duration, 'long')
    }

    return 'n/a';
  }, [podcast.duration]);

  return (
    <div className={styles.details}>
      <div className={cx(styles.field, styles.thumbnail)}>
        <div className={styles.inner}>
          <div className={styles.overlay}>
            <i className="paperflite-edit"></i>
          </div>
          <div
            className={cx(styles.field, styles.thumbnail)}
            onClick={() => {
              setEditThumbnail(true);
            }}
          >
            <div className={styles.inner}>
              <div className={styles.overlay}>
                <i className="paperflite-edit"></i>
              </div>
              <BannerView
                banner={banner as any}
                defaultBanner="/images/assets/default_asset.jpg"
              />
            </div>
            <Reveal show={bannerError != null}>
              <div className={styles.error}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>{bannerError?.message}</span>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal show={thumbnailError != null}>
          <div className={styles.error}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{thumbnailError?.message}</span>
          </div>
        </Reveal>
      </div>
      <div className={cx(styles.field, styles.name)}>
        <TextareaAutosize
          className={styles.input}
          ref={nameInputRef}
          placeholder="Enter Podcast name..."
          rows={1}
          maxRows={10}
          value={name}
          data-testid="podcast-name"
          onChange={handleNameChange}
          onPointerEnterCapture={() => { }}
          onPointerLeaveCapture={() => { }}
        />

        <div className={cx(styles.field, styles.configurations)}>
          <div className={styles.item}>
            <i className={cx(styles.duration, "paperflite-clock")} />
            <span>{podcastDuration}</span>
          </div>
          <div className={styles.item}>
            <span>Type:</span>
            <span>Conversational</span>
          </div>
        </div>

        <Reveal show={nameError != null}>
          <div className={styles.error}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{nameError?.message}</span>
          </div>
        </Reveal>
      </div>

      {editThumbnail && (
        <EditCourseThumbnail
          show={editThumbnail}
          banner={podcast?.banner || null}
          onFinish={handleThumbnailChange}
          onClose={() => setEditThumbnail(false)}
        />
      )}
    </div>
  );
};

interface PodcastDetailContainerProps {
  podcast: Podcast;
  // eslint-disable-next-line no-unused-vars
  onChange?: (property: string, value: any) => void;
}

const PodcastDetailContainer: React.FC<PodcastDetailContainerProps> = ({ podcast, onChange }) => {

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "podcast", property: "description" });
  });

  const topics = useMemo(() => {
    return _.get(podcast, "topics") || [];
  }, [podcast]);

  const description = useMemo(() => {
    return _.get(podcast, "description") || "";
  }, [podcast]);

  const handleDescriptionChange = (e: any) => {
    const content = e.target.value;
    onChange?.("description", content);
  };

  return (
    <div className={styles.editDetailsContainer}>
      <div className={cx(styles.field, styles.description)}>
        <div className={styles.label}>Description</div>
        <TextareaAutosize
          className={styles.descriptionInput}
          rows={1}
          value={description}
          placeholder={
            "Enter your podcast description here..."
          }
          spellCheck={true}
          onChange={handleDescriptionChange}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />

        <Reveal show={descriptionError != null}>
          <div className={styles.error}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{descriptionError?.message}</span>
          </div>
        </Reveal>
      </div>
      <div className={styles.topics}>
        <div className={styles.title}>Topics Covered</div>
        <div className={styles.topicsContent}>
          {topics.map((topic: any, index: number) => (
            <div key={index} className={styles.topic}>
              <span className={styles.index}>{String(index + 1).padStart(2, '0')}</span>
              <span className={styles.separator}>/</span>
              <span className={styles.name}>{topic.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
interface PodcastTranscriptsProps {
  transcripts: any[];
}

const PodcastTranscripts: React.FC<PodcastTranscriptsProps> = ({ transcripts }) => {

  //states
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  //animations
  const [childrenAnimation] = useSpring(
    {
      opacity: mountChildren ? 1 : 0,
      height: mountChildren ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [mountChildren, containerBounds],
  );

  const onExpand = useCallback(() => {
    setMountChildren((prev) => !prev);
  }, []);

  return (
    <div className={styles.transcriptContainer}>
      <div
        className={cx(styles.header, {
          [styles.expanded]: mountChildren,
        })}
        onClick={onExpand}
      >
        <span>Read along the podcast</span>
        <i className="paperflite-dropdown" />
      </div>
      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <div ref={containerRef} className={styles.transcripts}>
            {(transcripts || []).map((transcript, index) => {
              return (transcript.conversations || []).map(
                (conversation, idx) => {
                  return (
                    <div key={index} className={styles.transcript}>
                      <div className={styles.duration}>
                        {formatTime(conversation.start)}
                      </div>
                      <div className={styles.conversation}>
                        {conversation.text}
                      </div>
                    </div>
                  );
                },
              );
            })}
          </div>
        )}
      </animated.div>
    </div>
  );
};
